import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, RefreshCw, ChevronLeft, ChevronRight, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SUBMISSION_STATUSES,
  type SourceType,
} from "@shared/websiteSubmissions";
import {
  useWebsiteSubmissions,
  useWebsiteStats,
  useSyncWebsite,
  type SubmissionRow,
} from "@/hooks/useWebsiteSubmissions";
import { SubmissionDetail } from "@/components/website/SubmissionDetail";
import { WebsiteTabs } from "@/components/website/WebsiteTabs";

/**
 * The Website tab is a triage queue, not a record list.
 *
 * The job is deciding which of ~60 inbound leads deserves a call today, and for
 * an M&A firm the deciding signal is deal size. So magnitude is the organizing
 * principle: the estimate is the largest thing in each row, and a log-scaled
 * bar underneath it makes a $525K tyre-kicker visibly collapse next to a $26M
 * business. A flat table rendered both at identical weight, which is what made
 * the first version unreadable.
 *
 * Palette and type are inherited from the app (navy accent, gold primary, the
 * FinPanel eyebrow) on purpose — this page has to sit beside Buyer Network
 * without looking like a different product.
 */

const TAB_LABEL: Record<SourceType, string> = {
  valuation: "Valuations",
  subscriber: "Subscribers",
  buyer: "Buyers",
};

const DATE_LABEL: Record<SourceType, string> = {
  valuation: "Submitted",
  subscriber: "Subscribed",
  buyer: "Registered",
};

/**
 * Only deliberate progress earns a badge.
 *
 * `new` and `reviewed` are both resting states — nothing has happened to the
 * lead yet — so neither is worth a chip on every row. Badging `reviewed` put a
 * label on all 63 rows, which is the same noise the `new` badge was removed
 * for; the historical backfill lands as `reviewed`, so that is the common case,
 * not the exception.
 */
const BADGED = new Set(["contacted", "converted", "archived"]);

/** Only an archived row recedes. Dimming `reviewed` greyed out the whole list. */
const DIMMED = new Set(["archived"]);

const STATUS_TONE: Record<string, string> = {
  contacted: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  converted: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  archived: "bg-muted text-muted-foreground/70",
};

const ALL = "__all__";

const monthKey = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "Undated"
    : d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const dayOf = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function relative(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Log scale, because these span two orders of magnitude ($525K to $37.5M) and a
 * linear bar would render everything below $5M as an invisible sliver.
 */
function magnitudeFraction(value: number | null, max: number): number {
  if (!value || value <= 0 || max <= 0) return 0;
  // A quarter-million floor: below it the bar collapses to a stub, which is the
  // right reading for a firm that does not work deals that size.
  const floor = 250_000;
  const lo = Math.log10(floor);
  const hi = Math.log10(Math.max(max, floor * 10));
  return Math.min(1, Math.max(0.04, (Math.log10(Math.max(value, floor)) - lo) / (hi - lo)));
}

/** One queue row. Two lines: what it is, then who and when. */
function QueueRow({
  row,
  max,
  onOpen,
}: {
  row: SubmissionRow;
  max: number;
  onOpen: () => void;
}) {
  const badged = BADGED.has(row.status);
  const dimmed = DIMMED.has(row.status);
  // Valuations only. A buyer row's headline is its firm type, so a bar beneath
  // it reads as quantifying "Private Equity" — it is actually deal size, with
  // no number on the row to anchor it. An unlabeled bar is worse than none, and
  // a buyer's deal range lives in the detail view where it is labelled.
  const frac = row.source_type === "valuation" ? magnitudeFraction(row.magnitude, max) : 0;
  // Derive the two lines so nothing is printed twice. A subscriber has no
  // company, so its email becomes the headline — and naively appending the
  // contact line then rendered that same address again directly beneath itself.
  const primary =
    row.company_name || row.contact_name || row.contact_email || "Unnamed submission";
  const secondary = [row.contact_name, row.contact_email]
    .filter((v): v is string => Boolean(v) && v !== primary)
    .join(" · ");

  // Constant per tab, so it is noise in a list — the same reason the "new"
  // badge is gone. Subscribers show only a date on the right.
  const showHeadline = row.source_type !== "subscriber";

  return (
    <button
      onClick={onOpen}
      className={cn(
        // Three columns so the eye does not have to cross 1,000px of nothing to
        // get from a company name to its number. The middle carries the one
        // extra fact that changes a triage decision, and collapses on narrow
        // screens rather than squeezing the other two.
        "group grid w-full grid-cols-[minmax(0,1fr)_172px] items-start gap-4 border-b border-border/60 px-4 py-3 text-left transition-colors md:grid-cols-[minmax(0,1fr)_minmax(0,15rem)_172px]",
        "hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset",
        // Only an archived row recedes. Absence of decoration is the signal
        // for everything else, which is what let 63 identical pills go away.
        dimmed && "opacity-55"
      )}
    >
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{primary}</div>
        <div className="mt-0.5 flex items-center gap-2 truncate text-xs text-muted-foreground">
          {secondary && <span className="truncate">{secondary}</span>}
          {badged && (
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
                STATUS_TONE[row.status]
              )}
            >
              {row.status}
            </span>
          )}
        </div>
      </div>

      <div className="hidden min-w-0 md:block">
        {row.subtitle ? (
          <span className="line-clamp-2 text-xs leading-snug text-muted-foreground">
            {row.subtitle}
          </span>
        ) : null}
      </div>

      <div className="w-[172px] shrink-0">
        {showHeadline && (
          <div className="text-right text-sm font-semibold tabular-nums">
            {row.headline ?? <span className="font-normal text-muted-foreground/50">—</span>}
          </div>
        )}
        {/* The signature: magnitude made visible. Deal size is what this firm
            sorts the world by, so it gets the one graphic element on the page.
            Capped at 80px and sharing a line with the date — full-width it read
            as an underline rule fighting the row borders, and the differences
            between $5M and $27M were invisible at that length. */}
        <div className="mt-1 flex items-center justify-end gap-2">
          {frac > 0 && (
            <div className="h-[4px] w-20 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-accent/65"
                style={{ width: `${frac * 100}%` }}
              />
            </div>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">
            {dayOf(row.submitted_at)}
          </span>
        </div>
      </div>
    </button>
  );
}

export default function Website({ tab = "valuation" }: { tab?: SourceType }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState(ALL);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // A filter or tab change resets to page 1, or a narrowed result set strands
  // the user on an empty page 3 with no obvious way back.
  useEffect(() => setPage(1), [debounced, status, tab]);

  const stats = useWebsiteStats();
  const sync = useSyncWebsite();
  const list = useWebsiteSubmissions({
    type: tab,
    status: status === ALL ? undefined : status,
    q: debounced || undefined,
    page,
  });

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const pageSize = list.data?.pageSize ?? 50;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  const byType = (t: SourceType) => stats.data?.byType.find((b) => b.source_type === t);
  const current = byType(tab);

  const lastSynced = stats.data?.byType.reduce<string | null>(
    (acc, b) => (b.last_synced_at && (!acc || b.last_synced_at > acc) ? b.last_synced_at : acc),
    null
  );

  const max = useMemo(
    () => rows.reduce((m, r) => (r.magnitude && r.magnitude > m ? r.magnitude : m), 0),
    [rows]
  );

  // Month headers give a 60-row queue free scannability, and triage is
  // time-sensitive, so grouping by when it arrived encodes something true.
  const groups = useMemo(() => {
    const out: { label: string; rows: SubmissionRow[] }[] = [];
    for (const r of rows) {
      const label = monthKey(r.submitted_at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push(r);
      else out.push({ label, rows: [r] });
    }
    return out;
  }, [rows]);

  const handleSync = async () => {
    try {
      await sync.mutateAsync();
      toast.success("Synced from the website.");
    } catch (e: any) {
      toast.error(String(e.message ?? "").replace(/^\d+:\s*/, "") || "Sync failed.");
    }
  };

  /** Distinct states, so "loading" and "never synced" cannot be confused —
   *  the first version said "Synced never" while the data was still in flight. */
  const syncLabel = () => {
    if (sync.isPending) return "Syncing…";
    if (stats.isLoading) return "Checking…";
    if (stats.isError) return "Status unavailable";
    const rel = relative(lastSynced);
    return rel ? `Synced ${rel}` : "Not synced yet";
  };

  return (
    <AppLayout title="Website">
      <div className="space-y-5">
        {/* Stat strip. Replaces a title block that repeated the page name and
            told the reader nothing they could act on. */}
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
              {TAB_LABEL[tab]}
            </div>
            {/* One baseline. The review count used to be a floating chip beside
                this, which sat off the type's baseline and read as unrelated. */}
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">
                {stats.isLoading ? "—" : (current?.total ?? 0)}
              </span>
              <span className="text-sm text-muted-foreground">from firstturncapital.com</span>
              {current && current.unreviewed > 0 && (
                <>
                  <span className="text-sm text-muted-foreground/50">·</span>
                  <button
                    onClick={() => setStatus("new")}
                    className="text-sm font-medium text-primary-text hover:underline"
                  >
                    {current.unreviewed} new
                  </button>
                </>
              )}
            </div>
          </div>

          {stats.data && !stats.data.configured ? (
            <p className="flex max-w-sm items-start gap-1.5 text-xs text-muted-foreground">
              <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>
                Sync is off on this server. Set <code>CRM_SYNC_TOKEN</code> and{" "}
                <code>WEBSITE_SYNC_URL</code> to pull submissions.
              </span>
            </p>
          ) : (
            <button
              onClick={handleSync}
              disabled={sync.isPending}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
              title="Pull new submissions from the website"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", sync.isPending && "animate-spin")} />
              {syncLabel()}
            </button>
          )}
        </header>

        <WebsiteTabs
          active={tab}
          counts={Object.fromEntries((stats.data?.byType ?? []).map((b) => [b.source_type, b.total]))}
        />

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search name, email, company…"
              className="h-8 pl-8 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-[145px] text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {SUBMISSION_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s[0].toUpperCase() + s.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(debounced || status !== ALL) && (
            <button
              onClick={() => {
                setSearch("");
                setStatus(ALL);
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {total} shown · sorted by {DATE_LABEL[tab].toLowerCase()}
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {list.isLoading ? (
            <div className="divide-y divide-border/60">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center justify-between gap-4 px-4 py-3.5">
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </div>
          ) : list.isError ? (
            <div className="px-4 py-12 text-center">
              <p className="text-sm font-medium">Could not load submissions.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                The server did not answer. Try again in a moment.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => list.refetch()}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center">
              {debounced || status !== ALL ? (
                <>
                  <p className="text-sm font-medium">Nothing matches those filters.</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Clear the search or pick a different status.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">
                    No {TAB_LABEL[tab].toLowerCase()} yet.
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Sync pulls them in from the website every 15 minutes.
                  </p>
                </>
              )}
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.label}>
                <div className="sticky top-0 z-10 border-b border-border/60 bg-muted/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground backdrop-blur">
                  {g.label}
                </div>
                {g.rows.map((r) => (
                  <QueueRow key={r.id} row={r} max={max} onOpen={() => setSelectedId(r.id)} />
                ))}
              </div>
            ))
          )}
        </div>

        {lastPage > 1 && (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs tabular-nums text-muted-foreground">
              Page {page} of {lastPage}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page >= lastPage}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <SubmissionDetail id={selectedId} onClose={() => setSelectedId(null)} />
    </AppLayout>
  );
}
